import axios, { AxiosResponse } from 'axios'
import { User } from '../types/index'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: User
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  company?: string
  phone?: string
}

class AuthService {
  private baseURL: string

  constructor() {
    this.baseURL = `${API_URL}/auth`
  }

  private mapUser(u: any): User {
    return {
      id: u.id,
      email: u.email,
      firstName: u.first_name ?? u.firstName ?? '',
      lastName: u.last_name ?? u.lastName ?? '',
      role: u.role,
      isActive: u.is_active ?? u.isActive ?? true,
      lastLogin: u.last_login ?? u.lastLogin,
      createdAt: u.created_at ?? u.createdAt,
      updatedAt: u.updated_at ?? u.updatedAt,
      profile: typeof u.profile === 'string' ? JSON.parse(u.profile) : u.profile,
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    })
    
    const { token, refreshToken, user } = response.data.data
    
    // Store tokens
    localStorage.setItem('access_token', token)
    localStorage.setItem('refresh_token', refreshToken)
    
    return {
      accessToken: token,
      refreshToken,
      user: this.mapUser(user)
    }
  }

  async register(userData: RegisterData): Promise<LoginResponse> {
    const mappedData = {
      email: userData.email,
      password: userData.password,
      first_name: userData.firstName,
      last_name: userData.lastName,
      role: 'client' // Default role
    }
    
    const response = await apiClient.post('/auth/register', mappedData)
    const { user } = response.data.data
    
    // Auto-login after registration
    return this.login(userData.email, userData.password)
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout request failed:', error)
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get('/auth/profile')
    return this.mapUser(response.data.data.user)
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      // Clear any stale access token
      localStorage.removeItem('access_token')
      throw new Error('No refresh token found')
    }

    try {
      const response = await apiClient.post('/auth/refresh', {
        refreshToken,
      })

      const { token } = response.data.data
      localStorage.setItem('access_token', token)
      return token
    } catch (error) {
      // Clear both tokens on refresh failure
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      throw error
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email })
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/reset-password', {
      token,
      password,
    })
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    })
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await apiClient.put('/auth/profile', userData)
    return response.data.data
  }

  getToken(): string | null {
    return localStorage.getItem('access_token')
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token')
    if (!token) return false

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp > currentTime
    } catch {
      return false
    }
  }

  setupTokenRefresh(onTokenExpired: () => Promise<void>) {
    // Response interceptor for token refresh
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          // Check if we have a refresh token before attempting refresh
          const refreshToken = localStorage.getItem('refresh_token')
          if (!refreshToken) {
            // No refresh token, clear everything and reject
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            return Promise.reject(error)
          }

          try {
            await onTokenExpired()
            // Retry the original request with new token
            const newToken = localStorage.getItem('access_token')
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return apiClient(originalRequest)
            }
          } catch (refreshError) {
            // Token refresh failed, clear tokens
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            throw refreshError
          }
        }

        return Promise.reject(error)
      }
    )

    // Return cleanup function
    return () => {
      apiClient.interceptors.response.eject(interceptor)
    }
  }
}

export const authService = new AuthService()
